import { inject, Injectable } from '@angular/core';
import { EntitlementsCache, UNLIMITED } from '../models/entitlements.model';
import { DatabaseService, type QuerySignal } from './database.service';
import { LoggerService } from './logger.service';

/**
 * Reads this org's cached billing-plan limits (entitlements_cache table) so the
 * app can enforce asset/custom-form hard caps fully offline. This table is
 * written only by the server -- this service is read-only.
 */
@Injectable({
  providedIn: 'root',
})
export class EntitlementsService {
  private db = inject(DatabaseService);
  private logger = inject(LoggerService).getLogger('entitlements');

  /** Get the current org's entitlements row. Returns `null` if not yet seeded. */
  async getCurrent(): Promise<EntitlementsCache | null> {
    await this.db.init();
    const results = await this.db.exec('SELECT * FROM entitlements_cache LIMIT 1');
    if (results.length === 0) {
      this.logger.warn('No entitlements_cache row found for this org');
      return null;
    }
    return results[0] as EntitlementsCache;
  }

  /** Reactive query for the current org's entitlements row. */
  createQuery(): QuerySignal<EntitlementsCache | null> {
    return this.db.createQuery('entitlements_cache', () => this.getCurrent());
  }

  /** Whether a limit value (asset_limit/custom_form_limit) represents "unlimited". */
  isUnlimited(limit: number): boolean {
    return limit === UNLIMITED;
  }

  /** Whether `currentCount` has reached or exceeded `limit` (unlimited never reached). */
  limitReached(currentCount: number, limit: number): boolean {
    if (this.isUnlimited(limit)) {
      return false;
    }
    return currentCount >= limit;
  }
}
